import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Divider,
  Chip,
  useTheme,
  CircularProgress,
  Tooltip
} from "@mui/material";
import SetupStepHelp from "./setup/SetupStepHelp";

const EtapaPipeline = ({
  company,
  pipelineSteps,
  setPipelineStages,
  handleRemoveStep,
  generatePipelineStages
}) => {
  const theme = useTheme();
  const { t } = useTranslation("onboarding");
  const [loadingSugestao, setLoadingSugestao] = useState(false);
  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  const fetchPipelineSteps = async () => {
    setLoadingSugestao(true);
    try {
      const data = await generatePipelineStages(company); // Chama a API para gerar as sugestões

      // Transforma os dados para o formato { from, to } e adiciona o Tooltip com a descrição
      const formattedData = data.map((step, index) => ({
        from: step.title,  // O "title" será o "from"
        to: index < data.length - 1 ? data[index + 1].title : "",  // O "title" da próxima etapa será o "to"
        fromDescription: step.description,  // Armazenamos a descrição para "from" usar como Tooltip
        toDescription: index < data.length - 1 ? data[index + 1].description : ""  // Armazenamos a descrição para "to"
      }));

      formattedData.pop();
      setPipelineStages(formattedData); // Atualiza as etapas no estado
    } catch (error) {
      console.error("Erro ao gerar sugestões:", error);
    } finally {
      setLoadingSugestao(false);
    }
  };

  const handleAddNewStep = () => {
    const newStep = { from: pipelineSteps[pipelineSteps.length - 1]?.to || "", to: "" };
    setPipelineStages((prevSteps) => [...prevSteps, newStep]); // Adiciona uma nova etapa ao pipeline
  };

  const handleStepChange = (index, field, value) => {
    const updatedSteps = [...pipelineSteps];
    updatedSteps[index] = { ...updatedSteps[index], [field]: value };

    // Se o campo alterado for "to", também atualiza o "from" da próxima etapa
    if (field === "to" && index < updatedSteps.length - 1) {
      updatedSteps[index + 1].from = value;
    }

    setPipelineStages(updatedSteps); // Atualiza as etapas no estado
  };

  return (
    <Box sx={{ width: "100%" }}>
      <SetupStepHelp stepKey="pipeline" />
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: theme.palette.primary.main }}>
        🧭 {t("setup.pipelineStep.title")}
      </Typography>

      <Typography variant="body1" gutterBottom>
        {t("setup.pipelineStep.intro")}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("setup.pipelineStep.hint")}
      </Typography>

      <Box display="flex" gap={2} mb={2}>
        <Button
          variant="contained"
          disabled={loadingSugestao}
          onClick={fetchPipelineSteps}
        >
          {loadingSugestao ? <CircularProgress size={24} /> : t("setup.pipelineStep.generateAi")}
        </Button>
      </Box>

      {pipelineSteps.length > 0 ? (
        pipelineSteps.map((step, index) => (
          <Grid container spacing={2} alignItems="center" key={index} mb={1}>
            <Grid item xs={5}>
              <Tooltip title={step.fromDescription} arrow>
                <TextField
                  fullWidth
                  label="De"
                  value={step.from}
                  onChange={(e) => handleStepChange(index, "from", e.target.value)} // Atualiza a etapa 'De'
                  InputProps={{ readOnly: !!index }}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={5}>
              <Tooltip title={step.toDescription} arrow>
                <TextField
                  fullWidth
                  label={t("setup.pipelineStep.to")}
                  value={step.to}
                  onChange={(e) => handleStepChange(index, "to", e.target.value)} // Atualiza a etapa 'Para'
                />
              </Tooltip>
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleRemoveStep(index)} // Remove a etapa
                disabled={pipelineSteps.length <= 1}
              >
                {t("setup.pipelineStep.remove")}
              </Button>
            </Grid>
          </Grid>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("setup.pipelineStep.emptyStages")}
        </Typography>
      )}

      <Box mt={2}>
        <Button variant="contained" onClick={handleAddNewStep}>
          {t("setup.pipelineStep.addStep")}
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        👀 {t("setup.pipelineStep.previewTitle")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#f9f9f9",
          borderRadius: 2,
          p: 2,
          border: "1px solid #ccc",
          justifyContent: "center",
        }}
      >
        {pipelineSteps.length > 0 ? (
          [
            pipelineSteps[0]?.from,
            ...pipelineSteps.map((p) => p.to),
          ].map((step, idx) => (
            <React.Fragment key={idx}>
              <Chip label={step} color="primary" variant="outlined" />
              {idx < pipelineSteps.length - 1 && <Typography>→</Typography>}
            </React.Fragment>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("setup.pipelineStep.emptyPreview")}
          </Typography>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
        💡 {t("setup.pipelineStep.footer")}
      </Typography>
    </Box>
  );
};

export default EtapaPipeline;
